import React from 'react';
import styled from 'styled-components';
import {Typography, UeLink} from 'epic-ue-ui';

import {ExternalLink} from './ExternalLink';

const HomeSectionCardWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    box-sizing: border-box;
    padding-bottom: 40px;

    .text {
        .title {
            font-family: Inter;
            font-size: 18px;
            font-weight: 500;
            line-height: 25px;
            letter-spacing: -0.2px;
            margin-bottom: 8px;
            color: white;
        }

        .description {
            font-family: Inter;
            font-size: 14px;
            line-height: 20px;
            color: rgba(255, 255, 255, 0.6);
        }
    }

    .ue-link-wrap-span {
        transition: opacity 0.3s ease-out;
        &:hover {
            opacity: 0.7;
        }
    }

    ${p => p.theme.breakpoints.up('md')} {
        min-width: 192px; // exactly 2 cards for the smallest 'md' breakpoint
        padding-right: 32px;
        width: calc(100% / 3);
    }

    &.four {
        ${p => p.theme.breakpoints.up('xl')} {
            min-width: initial;
            width: calc(100% / 4);
            padding-right: 24px;
            margin-right: initial;
        }
    }

    &.five {
        ${p => p.theme.breakpoints.up('xl')} {
            min-width: initial;
            width: calc(100% / 5);
            padding-right: 24px;
            margin-right: initial;
        }
    }
`;

const LogoWrapper = styled.div<{desktopHeight?: number; mobileHeight?: number}>`
    height: ${p => p.mobileHeight ?? '40'}px;

    & > img {
        height: 100%;
        max-width: 100%;
    }

    ${p => p.theme.breakpoints.up('sm')} {
        height: ${p => p.desktopHeight ?? '50'}px;
    }
`;

const HomeSectionCardLinks = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const StyledLink = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
    font-family: Inter;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.2px;
    text-decoration: none;
    color: #117dff;
    transition: color 0.3s ease-out;

    svg {
        transition: transform 0.4s ease-out;
    }

    &:hover {
        color: #58a9ff;

        svg {
            transform: translate3d(0px, 0px, 0px) translate(1px, -1px);
        }
    }
`;

export interface HomeSectionCardLink {
    title: string;
    href: string;
}

export interface HomeSectionCardProps {
    logo: string;
    logoAlt?: string;
    logoBoxHeight?: {
        desktop?: number;
        mobile?: number;
    };
    title: string;
    titleHref?: string;
    description?: string;
    links?: HomeSectionCardLink[];
    numCards: number;
}

export const HomeSectionCard: React.FC<HomeSectionCardProps> = ({logo, logoAlt = 'Logo', logoBoxHeight, title, titleHref, description, links, numCards}): JSX.Element => {
    let cardSizeClass = 'three';
    if (numCards % 5 === 0) {
        cardSizeClass = 'five';
    } else if (numCards % 4 === 0 || (numCards > 6 && numCards % 4 === 1)) {
        cardSizeClass = 'four';
    }

    return (
        <HomeSectionCardWrapper className={`card ${cardSizeClass}`}>
            <UeLink href={titleHref}>
                <LogoWrapper desktopHeight={logoBoxHeight?.desktop} mobileHeight={logoBoxHeight?.mobile}>
                    <img src={logo} alt={logoAlt} loading="lazy" />
                </LogoWrapper>
            </UeLink>

            <div className="text">
                <UeLink href={titleHref} target="_blank">
                    <Typography variant="h2" className="title">
                        {title}
                    </Typography>
                </UeLink>
                {description && (
                    <Typography variant="p" className="description">
                        {description}
                    </Typography>
                )}
            </div>
            <HomeSectionCardLinks>
                {links?.map(({title, href}, i) => (
                    <UeLink key={`${title}_${i}`} href={href} target="_blank">
                        <StyledLink>
                            {title}
                            <ExternalLink />
                        </StyledLink>
                    </UeLink>
                ))}
            </HomeSectionCardLinks>
        </HomeSectionCardWrapper>
    );
};
